import {
  CheckboxIcon,
  CodeIcon,
  HeadingIcon,
  ImageIcon,
  ListBulletIcon,
  QuoteIcon,
  TableIcon,
  TextIcon,
  VideoIcon,
} from '@radix-ui/react-icons'
import { CommandCategory } from '../slash-command'
import { AIIcon } from '~/components/icons/ai'
import { Editor } from '@tiptap/core'

export const getRichTextSlashCommands = ({
  teamId,
  repoId,
  startImageUpload,
  startVideoUpload,
  triggerCompletion,
}: {
  teamId: string
  repoId: string
  triggerCompletion: (editor: Editor) => void
  startImageUpload: (
    file: File,
    view: any,
    startPos: number,
    options: { teamId: string; repoId: string }
  ) => void
  startVideoUpload: (
    file: File,
    view: any,
    startPos: number,
    options: { teamId: string; repoId: string }
  ) => void
}): CommandCategory[] => [
  {
    id: 'basic',
    name: 'Basic',
    commands: [
      {
        id: 'ai',
        title: 'Continue writing',
        description: 'Use AI to expand your thoughts.',
        keywords: ['gpt', 'ai', 'generate'],
        icon: <AIIcon className="w-7 !text-inherit" />,
        command: ({ editor, range, onExit }) => {
          editor.chain().focus().deleteRange(range).run()
          triggerCompletion(editor)
          onExit()
        },
      },
      {
        id: 'heading1',
        title: 'Heading 1',
        tipTapKey: 'h1',
        description: 'Large section heading.',
        keywords: ['title', 'big', 'large', 'h1'],
        icon: <HeadingIcon className="w-7" />,
        command: ({ editor, range }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .setNode('heading', { level: 1 })
            .run()
        },
      },
      {
        id: 'heading2',
        title: 'Heading 2',
        tipTapKey: 'h2',
        description: 'Medium section heading.',
        keywords: ['subtitle', 'medium', 'h2'],
        icon: <HeadingIcon className="w-7" />,
        command: ({ editor, range }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .setNode('heading', { level: 2 })
            .run()
        },
      },
      {
        id: 'heading3',
        title: 'Heading 3',
        tipTapKey: 'h3',
        description: 'Small section heading.',
        keywords: ['subtitle', 'small', 'h3'],
        icon: <HeadingIcon className="w-7" />,
        command: ({ editor, range }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .setNode('heading', { level: 3 })
            .run()
        },
      },
      {
        id: 'heading4',
        title: 'Heading 4',
        tipTapKey: 'h4',
        description: 'Small section heading.',
        keywords: ['subtitle', 'small', 'h4'],
        icon: <HeadingIcon className="w-7" />,
        command: ({ editor, range }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .setNode('heading', { level: 4 })
            .run()
        },
      },
      {
        id: 'heading5',
        title: 'Heading 5',
        tipTapKey: 'h5',
        description: 'Small section heading.',
        keywords: ['subtitle', 'small', 'h5'],
        icon: <HeadingIcon className="w-7" />,
        command: ({ editor, range }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .setNode('heading', { level: 5 })
            .run()
        },
      },
      {
        id: 'heading6',
        title: 'Heading 6',
        tipTapKey: 'h6',
        description: 'Small section heading.',
        keywords: ['subtitle', 'small', 'h6'],
        icon: <HeadingIcon className="w-7" />,
        command: ({ editor, range }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .setNode('heading', { level: 6 })
            .run()
        },
      },
      {
        id: 'paragraph',
        title: 'Text',
        description: 'Just start typing with plain text.',
        keywords: ['p', 'paragraph'],
        icon: <TextIcon className="w-7" />,
        command: ({ editor, range }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .toggleNode('paragraph', 'paragraph')
            .run()
        },
      },

      {
        id: 'bulletList',
        title: 'Bullet List',
        tipTapKey: 'bulletList',
        description: 'Create a simple bullet list.',
        keywords: ['unordered', 'point'],
        icon: <ListBulletIcon className="w-7" />,
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleBulletList().run()
        },
      },

      {
        id: 'numberedList',
        title: 'Numbered List',
        tipTapKey: 'orderedList',
        description: 'Create a list with numbering.',
        keywords: ['ordered'],
        icon: <ListBulletIcon className="w-7" />,
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleOrderedList().run()
        },
      },
      {
        id: 'taskList',
        title: 'To-do List',
        tipTapKey: 'taskList',
        description: 'Track tasks with a to-do list.',
        keywords: ['todo', 'task', 'list', 'check', 'checkbox'],
        icon: <CheckboxIcon className="w-7" />,
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleTaskList().run()
        },
      },
      {
        id: 'quote',
        title: 'Quote',
        tipTapKey: 'blockquote',
        description: 'Capture a quote.',
        keywords: ['blockquote'],
        icon: <QuoteIcon className="w-7" />,
        command: ({ editor, range }) =>
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .toggleNode('paragraph', 'paragraph')
            .toggleBlockquote()
            .run(),
      },
      {
        id: 'code',
        title: 'Code',
        tipTapKey: 'codeBlock',
        description: 'Capture a code snippet.',
        keywords: ['codeblock'],
        icon: <CodeIcon className="w-7" />,
        command: ({ editor, range }) =>
          editor.chain().focus().deleteRange(range).toggleCodeBlock().run(),
      },
      {
        id: 'image',
        title: 'Image',
        tipTapKey: 'image',
        description: 'Upload an image from your computer.',
        keywords: ['photo', 'picture', 'media', 'asset'],
        icon: <ImageIcon className="w-7" />,
        command: ({ editor }) => {
          if (!teamId || !repoId) {
            throw new Error('Team or Repository not found')
          }
          // upload image
          const input = document.createElement('input')
          input.type = 'file'
          input.accept = 'image/*'
          input.onchange = async () => {
            if (input.files?.length) {
              const file = input.files[0]
              if (!file) return
              let startPos: number
              const selection = editor.view.state.selection
              const nodeBefore = editor.view.state.selection.$head.nodeBefore
              if (
                nodeBefore &&
                nodeBefore.isText &&
                nodeBefore.text?.startsWith('/')
              ) {
                // insert content below and delete the slash
                const start = selection.$head.pos - nodeBefore.text!.length
                editor.chain().deleteNode('paragraph').focus().run() // Delete the slash "/" and the previous text
                startPos = start - 1
              } else {
                // delete the slash and add in the same line
                editor
                  .chain()
                  .deleteRange({
                    from:
                      selection.$head.pos -
                      (nodeBefore?.text?.split('/').pop()?.length ?? 0) -
                      1,
                    to: selection.$head.pos,
                  })
                  .run()
                startPos =
                  selection.$head.pos -
                  (nodeBefore?.text?.split('/').pop()?.length ?? 0) -
                  1
              }

              startImageUpload(file, editor.view, startPos, {
                teamId,
                repoId,
              })
            }
          }

          input.click()
        },
      },
      {
        id: 'video',
        title: 'Video',
        tipTapKey: 'video',
        description: 'Upload a video from your computer.',
        keywords: ['film', 'record', 'recording', 'media', 'asset'],
        icon: <VideoIcon className="w-7" />,
        command: ({ editor }) => {
          if (!teamId || !repoId) {
            throw new Error('Team or Repository not found')
          }
          // upload image
          const input = document.createElement('input')
          input.type = 'file'
          input.accept = 'video/*'
          input.onchange = async () => {
            if (input.files?.length) {
              const file = input.files[0]
              if (!file) return
              let startPos: number
              const selection = editor.view.state.selection
              const nodeBefore = editor.view.state.selection.$head.nodeBefore

              if (
                nodeBefore &&
                nodeBefore.isText &&
                nodeBefore.text?.startsWith('/')
              ) {
                // insert content below and delete the slash

                const start = selection.$head.pos - nodeBefore.text!.length

                editor.chain().deleteNode('paragraph').focus().run() // Delete the slash "/" and the previous text
                startPos = start - 1
              } else {
                // delete the slash and add in the same line

                editor
                  .chain()
                  .deleteRange({
                    from:
                      selection.$head.pos -
                      (nodeBefore?.text?.split('/').pop()?.length ?? 0) -
                      1,
                    to: selection.$head.pos,
                  })
                  .run()
                startPos =
                  selection.$head.pos -
                  (nodeBefore?.text?.split('/').pop()?.length ?? 0) -
                  1
              }
              startVideoUpload(file, editor.view, startPos, {
                teamId,
                repoId,
              })
            }
          }
          input.click()
        },
      },
      {
        id: 'table',
        title: 'Table',
        tipTapKey: 'table',
        description: 'Create a simple table.',
        keywords: ['table', 'row', 'column', 'grid', 'list', 'collection'],
        icon: <TableIcon className="w-7" />,
        command: ({ editor, range }) =>
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
            .run(),
      },
    ],
  },
]
